import { FlagType, Item, Revision } from "./model";

export { toQuarter } from "./date";

/**
 * Reconstructing a past state of the radar needs no extra data: every revision
 * carries its own `ring`, `quadrant` and `body`, which the generator enforces
 * (see `checkAttributes`). These helpers are pure so they can be unit tested
 * without a DOM or a router.
 */

const byReleaseDesc = (a: Revision, b: Revision) =>
  a.release < b.release ? 1 : a.release > b.release ? -1 : 0;

/** Revisions of an item published at or before `release`, newest first. */
const revisionsUntil = (item: Item, release: string): Revision[] =>
  item.revisions
    .filter((revision) => revision.release <= release)
    .slice()
    .sort(byReleaseDesc);

/** Oldest release of a radar, or `undefined` when it has none. */
export const initialRelease = (releases: string[]): string | undefined =>
  releases.slice().sort()[0];

/** Newest release of a radar, or `undefined` when it has none. */
export const latestRelease = (releases: string[]): string | undefined =>
  releases.slice().sort()[releases.length - 1];

/**
 * The oldest release of a radar is an **initialisation**, not a wave of
 * arrivals: by construction every one of its items has its first revision
 * there. Flagging them all `new` says nothing - if everything is new, nothing
 * is - and drowns the very signal the timeline exists to make readable.
 */
export const isInitialRelease = (release: string, releases: string[]) =>
  releases.length > 0 && release === initialRelease(releases);

const flagAtRelease = (revisions: Revision[], release: string): FlagType => {
  const first = revisions[revisions.length - 1];
  const latest = revisions[0];

  if (first.release === release) {
    return FlagType.new;
  }
  if (latest.release === release && revisions.length > 1) {
    return FlagType.changed;
  }
  return FlagType.default;
};

/**
 * The radar as it looked at `release`.
 *
 * - existence: at least one revision at or before `release`
 * - ring / quadrant / tags: taken from the most recent revision <= `release`
 * - body: the most recent non empty body <= `release`
 * - flag: recomputed *relatively to `release`*, so browsing Q1 2025 shows what
 *   was new back then, not what is new today - except at the initial release,
 *   where nothing is flagged at all (see `isInitialRelease`)
 *
 * `releases` is the radar's full, ordered release list. It is required rather
 * than derived from `items` because a tag filter can hide every item of the
 * initial release and would otherwise make a later quarter look like the first.
 */
export const itemsAtRelease = (
  items: Item[],
  release: string,
  releases: string[]
): Item[] => {
  const initial = isInitialRelease(release, releases);

  return items.reduce<Item[]>((acc, item) => {
    const revisions = revisionsUntil(item, release);
    if (revisions.length === 0) {
      return acc;
    }

    const latest = revisions[0];
    const bodyRevision = revisions.find(
      (revision) => (revision.body || "").trim() !== ""
    );

    acc.push({
      ...item,
      ring: latest.ring,
      quadrant: latest.quadrant,
      title: latest.title || item.title || item.name,
      tags: latest.tags || item.tags,
      body: bodyRevision ? bodyRevision.body : item.body,
      flag: initial ? FlagType.default : flagAtRelease(revisions, release),
      revisions,
    });
    return acc;
  }, []);
};

export type ReleaseSummary = {
  /** True when `release` is the radar's initial state rather than a movement. */
  initial: boolean;
  /** Number of entries present at `release`. */
  total: number;
  added: Item[];
  changed: Item[];
};

/**
 * What happened at `release`: entries that appeared and entries that moved.
 *
 * At the initial release there is nothing to compare against, so `added` and
 * `changed` are empty and only `total` is meaningful.
 */
export const releaseSummary = (
  items: Item[],
  release: string,
  releases: string[]
): ReleaseSummary => {
  const atRelease = itemsAtRelease(items, release, releases);

  return {
    initial: isInitialRelease(release, releases),
    total: atRelease.length,
    added: atRelease.filter((item) => item.flag === FlagType.new),
    changed: atRelease.filter((item) => item.flag === FlagType.changed),
  };
};

/**
 * Resolves the release to display from a raw `?release=` query param.
 * Anything unknown falls back to the newest published release.
 */
export const resolveRelease = (
  releases: string[],
  requested?: unknown
): string | undefined => {
  if (typeof requested === "string" && releases.includes(requested)) {
    return requested;
  }
  return latestRelease(releases);
};
